/**
 * Toggle — iOS-style on/off switch. Drop-in replacement for native checkboxes.
 *
 * Props:
 *   checked        — boolean
 *   onChange       — (next: boolean) => void
 *   disabled       — boolean
 *   label          — optional text rendered to the right of the switch (clickable)
 *   size           — 'sm' (32x18) | 'md' (40x22, default) | 'lg' (48x26)
 *   activeColor    — override the on-state color (default InteGolf green)
 *
 * Usage:
 *   <Toggle checked={x} onChange={setX} label="Send me emails" />
 *
 * Why this exists: native checkboxes are visually clunky in admin/portal UIs
 * and Todd specifically asked to standardize on toggles everywhere.
 */
const SIZES = {
  sm: { w: 32, h: 18, knob: 14, pad: 2 },
  md: { w: 40, h: 22, knob: 18, pad: 2 },
  lg: { w: 48, h: 26, knob: 22, pad: 2 },
}

export default function Toggle({
  checked,
  onChange,
  disabled = false,
  label,
  size = 'md',
  activeColor = '#00d19f',
}) {
  const s = SIZES[size] || SIZES.md
  const offset = s.w - s.knob - s.pad
  const flip = () => { if (!disabled) onChange(!checked) }

  const sw = (
    <button
      type="button"
      onClick={flip}
      disabled={disabled}
      role="switch"
      aria-checked={checked}
      style={{
        width: s.w, height: s.h, borderRadius: s.h / 2,
        border: 'none', padding: 0, position: 'relative',
        background: checked ? activeColor : '#cbd5e1',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background .15s',
        flexShrink: 0,
      }}
    >
      <span style={{
        position: 'absolute', top: s.pad,
        left: checked ? offset : s.pad,
        width: s.knob, height: s.knob, borderRadius: s.knob / 2,
        background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        transition: 'left .15s',
      }} />
    </button>
  )

  if (!label) return sw
  return (
    <label style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: 13, color: '#0f172a',
      userSelect: 'none',
    }}>
      {sw}
      <span onClick={(e) => { e.preventDefault(); flip() }}>{label}</span>
    </label>
  )
}
